/**
 * Loads @kusto/monaco-kusto's AMD contribution exactly once. Must run AFTER
 * monaco itself is loaded (the @monaco-editor/react loader guarantees that
 * when called from an editor onMount / beforeMount path).
 *
 * The contribution registers the `'kusto'` Monaco language id (tokenizer,
 * completion/hover/signature-help providers, worker wiring) — see
 * `Docs/superpowers/plans/2026-07-08-kusto-NOTES.md` §S0/§S4 for how its AMD
 * bundle is served (under `<base>monaco/vs/language/kusto/`, physically
 * inside the served `vs` tree — required for the worker to resolve its own
 * module ids).
 */
let loadPromise: Promise<void> | null = null;

export function ensureKustoLanguageLoaded(): Promise<void> {
  if (loadPromise) return loadPromise;
  // The Promise executor runs synchronously inside `new Promise(...)`, so any
  // `loadPromise = null` written from within it is immediately clobbered by the
  // outer `loadPromise = <this promise>` assignment below — which previously
  // left a rejected promise memoised forever (a failed AMD load poisoned every
  // later retry). Reset the memo from a `.catch` on the settled promise instead,
  // so a call made once the AMD loader is ready can retry.
  const promise = new Promise<void>((resolve, reject) => {
    const amdRequire = (
      window as unknown as {
        require?: (deps: string[], ok: () => void, err?: (e: unknown) => void) => void;
      }
    ).require;
    if (!amdRequire) {
      reject(new Error('Monaco AMD loader not ready'));
      return;
    }
    amdRequire(
      ['vs/language/kusto/monaco.contribution'],
      () => resolve(),
      (e) => reject(e),
    );
  });
  promise.catch(() => {
    if (loadPromise === promise) loadPromise = null;
  });
  loadPromise = promise;
  return loadPromise;
}
